import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getInstancesOfClass,
  getInstanceCount,
  getBatchedInstanceCounts,
  getInstanceProperties,
  getInstanceTypes,
  getOutgoingRelationships,
  getIncomingRelationships,
  searchInstances,
  getInstancesByDomain,
  getGraphData,
} from '@/lib/sparql/queries'
import { update } from '@/lib/api/sparql-adapter'
import type { OntologyDomain, OntologyInstance, SparqlBinding } from '@/types/ontology'
import { getLocalName, getDomainFromUri } from '@/lib/ontology/namespaces'

function bindingsToInstances(bindings: SparqlBinding[], typeUri?: string): OntologyInstance[] {
  return bindings.map((binding) => {
    const uri = binding.instance?.value || ''
    const type = binding.type?.value || typeUri || ''
    const domain = getDomainFromUri(type) || 'solution'

    return {
      uri,
      type,
      typeLabel: getLocalName(type),
      domain,
      label: binding.label?.value || getLocalName(uri),
      properties: {},
    }
  })
}

export function useInstancesOfClass(classUri: string | null, limit = 100, offset = 0) {
  return useQuery({
    queryKey: ['instances', classUri, limit, offset],
    queryFn: async () => {
      if (!classUri) return []
      const results = await getInstancesOfClass(classUri, limit, offset)
      return bindingsToInstances(results.results.bindings, classUri)
    },
    enabled: !!classUri,
  })
}

export function useInstanceCount(classUri: string | null) {
  return useQuery({
    queryKey: ['instanceCount', classUri],
    queryFn: async () => {
      if (!classUri) return 0
      return getInstanceCount(classUri)
    },
    enabled: !!classUri,
  })
}

/**
 * Fetches instance counts for ALL classes in a single batched query.
 * This dramatically reduces SPARQL query load from ~40 queries to 1.
 */
export function useAllInstanceCounts(classUris: string[], enabled = true) {
  return useQuery({
    queryKey: ['allInstanceCounts', classUris.sort().join(',')],
    queryFn: async () => {
      if (classUris.length === 0) return {}
      return getBatchedInstanceCounts(classUris)
    },
    enabled: enabled && classUris.length > 0,
    staleTime: 30000, // 30 seconds
  })
}

export function useInstanceDetails(instanceUri: string | null) {
  return useQuery({
    queryKey: ['instanceDetails', instanceUri],
    queryFn: async () => {
      if (!instanceUri) return null

      const [typesResult, propsResult, outgoingResult, incomingResult] = await Promise.all([
        getInstanceTypes(instanceUri),
        getInstanceProperties(instanceUri),
        getOutgoingRelationships(instanceUri),
        getIncomingRelationships(instanceUri),
      ])

      const types = typesResult.results.bindings.map((b) => ({
        uri: b.type?.value || '',
        label: b.label?.value || getLocalName(b.type?.value || ''),
      }))

      const properties: Record<string, { value: string; label?: string }[]> = {}
      propsResult.results.bindings.forEach((b) => {
        const propUri = b.property?.value || ''
        if (!properties[propUri]) {
          properties[propUri] = []
        }
        properties[propUri].push({
          value: b.value?.value || '',
          label: b.valueLabel?.value,
        })
      })

      const outgoing = outgoingResult.results.bindings.map((b) => ({
        property: b.property?.value || '',
        target: b.target?.value || '',
        targetLabel: b.targetLabel?.value || getLocalName(b.target?.value || ''),
        targetType: b.targetType?.value || '',
      }))

      const incoming = incomingResult.results.bindings.map((b) => ({
        source: b.source?.value || '',
        sourceLabel: b.sourceLabel?.value || getLocalName(b.source?.value || ''),
        sourceType: b.sourceType?.value || '',
        property: b.property?.value || '',
      }))

      return {
        uri: instanceUri,
        types,
        properties,
        outgoing,
        incoming,
      }
    },
    enabled: !!instanceUri,
  })
}

export function useSearchInstances(searchTerm: string) {
  return useQuery({
    queryKey: ['searchInstances', searchTerm],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2) return []
      const results = await searchInstances(searchTerm)
      return bindingsToInstances(results.results.bindings)
    },
    enabled: searchTerm.length >= 2,
  })
}

export function useInstancesByDomain(domain: OntologyDomain | null, limit = 100) {
  return useQuery({
    queryKey: ['instancesByDomain', domain, limit],
    queryFn: async () => {
      if (!domain) return []
      const results = await getInstancesByDomain(domain, limit)
      return bindingsToInstances(results.results.bindings)
    },
    enabled: !!domain,
  })
}

export function useCreateInstance() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      typeUri,
      label,
      properties,
    }: {
      typeUri: string
      label: string
      properties: Record<string, string>
    }) => {
      const instanceUri = `${typeUri.split('#')[0]}#${label.replace(/\s+/g, '_')}_${Date.now()}`

      let sparql = `
        INSERT DATA {
          <${instanceUri}> a <${typeUri}> .
          <${instanceUri}> rdfs:label "${label}" .
      `

      for (const [propUri, value] of Object.entries(properties)) {
        if (value.startsWith('http://') || value.startsWith('https://')) {
          sparql += `  <${instanceUri}> <${propUri}> <${value}> .\n`
        } else {
          sparql += `  <${instanceUri}> <${propUri}> "${value}" .\n`
        }
      }

      sparql += '}'

      await update(sparql)
      return instanceUri
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] })
      queryClient.invalidateQueries({ queryKey: ['instanceCount'] })
      queryClient.invalidateQueries({ queryKey: ['instancesByDomain'] })
      queryClient.invalidateQueries({ queryKey: ['canvasInstances'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
      queryClient.invalidateQueries({ queryKey: ['domainInstanceCount'] })
    },
  })
}

export function useDeleteInstance() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (instanceUri: string) => {
      // Use a regular DELETE/WHERE update instead of DELETE WHERE so the
      // backend can safely scope it with WITH <projectGraph>.
      await update(`
        DELETE {
          <${instanceUri}> ?outPredicate ?outObject .
          ?inSubject ?inPredicate <${instanceUri}> .
        }
        WHERE {
          {
            <${instanceUri}> ?outPredicate ?outObject .
          }
          UNION
          {
            ?inSubject ?inPredicate <${instanceUri}> .
          }
        }
      `)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] })
      queryClient.invalidateQueries({ queryKey: ['instanceCount'] })
      queryClient.invalidateQueries({ queryKey: ['instancesByDomain'] })
      queryClient.invalidateQueries({ queryKey: ['instanceDetails'] })
      queryClient.invalidateQueries({ queryKey: ['canvasInstances'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

export function useUpdateInstanceLabel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ instanceUri, newLabel }: { instanceUri: string; newLabel: string }) => {
      // Delete old label and insert new one
      await update(`
        DELETE { <${instanceUri}> rdfs:label ?oldLabel }
        INSERT { <${instanceUri}> rdfs:label "${newLabel.replace(/"/g, '\\"')}" }
        WHERE { OPTIONAL { <${instanceUri}> rdfs:label ?oldLabel } }
      `)
    },
    onSuccess: (_, { instanceUri }) => {
      queryClient.invalidateQueries({ queryKey: ['instances'] })
      queryClient.invalidateQueries({ queryKey: ['instanceDetails', instanceUri] })
      queryClient.invalidateQueries({ queryKey: ['instancesByDomain'] })
      queryClient.invalidateQueries({ queryKey: ['canvasInstances'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

export function useUpdateInstanceProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      instanceUri,
      propertyUri,
      oldValue,
      newValue,
      isUri = false,
    }: {
      instanceUri: string
      propertyUri: string
      oldValue: string
      newValue: string
      isUri?: boolean
    }) => {
      const oldValueFormatted = oldValue.startsWith('http://') || oldValue.startsWith('https://')
        ? `<${oldValue}>`
        : `"${oldValue.replace(/"/g, '\\"')}"`
      const newValueFormatted = isUri || newValue.startsWith('http://') || newValue.startsWith('https://')
        ? `<${newValue}>`
        : `"${newValue.replace(/"/g, '\\"')}"`

      await update(`
        DELETE { <${instanceUri}> <${propertyUri}> ${oldValueFormatted} }
        INSERT { <${instanceUri}> <${propertyUri}> ${newValueFormatted} }
        WHERE { <${instanceUri}> <${propertyUri}> ${oldValueFormatted} }
      `)
    },
    onSuccess: (_, { instanceUri }) => {
      queryClient.invalidateQueries({ queryKey: ['instanceDetails', instanceUri] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

export function useAddInstanceProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      instanceUri,
      propertyUri,
      value,
      isUri = false,
    }: {
      instanceUri: string
      propertyUri: string
      value: string
      isUri?: boolean
    }) => {
      const valueFormatted = isUri || value.startsWith('http://') || value.startsWith('https://')
        ? `<${value}>`
        : `"${value.replace(/"/g, '\\"')}"`

      await update(`
        INSERT DATA {
          <${instanceUri}> <${propertyUri}> ${valueFormatted} .
        }
      `)
    },
    onSuccess: (_, { instanceUri }) => {
      queryClient.invalidateQueries({ queryKey: ['instanceDetails', instanceUri] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

export function useRemoveInstanceProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      instanceUri,
      propertyUri,
      value,
    }: {
      instanceUri: string
      propertyUri: string
      value: string
    }) => {
      const valueFormatted = value.startsWith('http://') || value.startsWith('https://')
        ? `<${value}>`
        : `"${value.replace(/"/g, '\\"')}"`

      await update(`
        DELETE DATA {
          <${instanceUri}> <${propertyUri}> ${valueFormatted} .
        }
      `)
    },
    onSuccess: (_, { instanceUri }) => {
      queryClient.invalidateQueries({ queryKey: ['instanceDetails', instanceUri] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

export interface GraphNode {
  uri: string
  label: string
  type: string
  typeLabel: string
  domain: OntologyDomain
}

export interface GraphEdge {
  source: string
  target: string
  predicate: string
  predicateLabel: string
}

export interface GraphDataResult {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

export function useGraphData(enabled = true, limit = 200) {
  return useQuery({
    queryKey: ['graphData', limit],
    queryFn: async (): Promise<GraphDataResult> => {
      const results = await getGraphData(limit)

      const nodesMap = new Map<string, GraphNode>()
      const edges: GraphEdge[] = []

      results.results.bindings.forEach((binding) => {
        const subjectUri = binding.subject?.value || ''
        const subjectType = binding.subjectType?.value || ''
        const objectUri = binding.object?.value || ''
        const objectType = binding.objectType?.value || ''

        // Add subject node
        if (subjectUri && !nodesMap.has(subjectUri)) {
          nodesMap.set(subjectUri, {
            uri: subjectUri,
            label: binding.subjectLabel?.value || getLocalName(subjectUri),
            type: subjectType,
            typeLabel: getLocalName(subjectType),
            domain: getDomainFromUri(subjectType) || 'solution',
          })
        }

        // Add object node
        if (objectUri && !nodesMap.has(objectUri)) {
          nodesMap.set(objectUri, {
            uri: objectUri,
            label: binding.objectLabel?.value || getLocalName(objectUri),
            type: objectType,
            typeLabel: getLocalName(objectType),
            domain: getDomainFromUri(objectType) || 'solution',
          })
        }

        // Add edge
        const predicate = binding.predicate?.value || ''
        if (subjectUri && objectUri && predicate) {
          edges.push({
            source: subjectUri,
            target: objectUri,
            predicate,
            predicateLabel: binding.predicateLabel?.value || getLocalName(predicate),
          })
        }
      })

      return {
        nodes: Array.from(nodesMap.values()),
        edges,
      }
    },
    enabled,
  })
}
